import { Injectable } from "@angular/core";
import {
  MatDialog as MatDialog,
  MatDialogRef as MatDialogRef,
} from "@angular/material/dialog";
import { Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { NGXLogger } from "ngx-logger";
import { Observable } from "rxjs";
import { PadlockService } from "../padlock/padlock.service";
import { GetPadlockTokenSuccess } from "../padlock/state/padlock-token.actions";
import { FORCE_PASSWORD_CHANGE_ROUTE } from "./temporary-password.guard";
import { AuthDialogComponent } from "./auth-dialog/auth-dialog.component";
import { B2cService } from "./b2c.service";
import { SetGuardedRoute } from "./state/auth/auth.actions";
import { AuthState } from "./state/auth/auth.reducer";
import { selectAuthState } from "./state/auth/auth.selectors";

@Injectable({ providedIn: "root" })
export class AuthService {
  auth$: Observable<AuthState> = this.store.select(selectAuthState);
  authState: AuthState;
  dialogRef: MatDialogRef<AuthDialogComponent>;
  timer: number;

  constructor(
    private store: Store<any>,
    private padlock: PadlockService,
    private b2cService: B2cService,
    private logger: NGXLogger,
    private dialog: MatDialog,
    private router: Router,
  ) {
    this.auth$.subscribe((state) => (this.authState = state));
  }

  validateToken(idpParam?: string) {
    this.logger.debug(`AUTH: validate token`);
    const isTokenValid = this.padlock.isTokenValid();
    const b2cRedirect = this.b2cService.getB2cRedirect();

    if (!isTokenValid) {
      if (!b2cRedirect && !idpParam) {
        this.popDialog();
      }
    } else {
      this.unpopDialog();
      const millisUntilExpiry = this.padlock.getMillisUntilExpiry();
      this.setReAuthTimeout(millisUntilExpiry); // TODO; also a bug! This is refreshing the timer every time!
    }
    return isTokenValid;
  }

  logout() {
    // To logout we clear the padlock token
    this.b2cService.logoutB2C();
    this.store.dispatch(new GetPadlockTokenSuccess(null));
    this.store.dispatch(new SetGuardedRoute(this.router.url));
  }

  unpopDialog() {
    const b2cRedirect = this.b2cService.getB2cRedirect();
    if (b2cRedirect) {
      this.b2cService.removeB2cRedirect();
      this.router.navigateByUrl(this.resolvePostLoginRoute(b2cRedirect));
      return;
    }

    if (this.dialogRef) {
      this.dialogRef.close();

      if (this.padlock.updateUserAndCheckIfChanged()) {
        this.router.navigateByUrl("/");
      } else {
        this.router.navigateByUrl(
          this.resolvePostLoginRoute(this.authState?.guardedRoute),
        );
      }
    }
    this.dialogRef = null;
  }

  private resolvePostLoginRoute(route: string | null | undefined): string {
    const fallback = "/";
    if (!route) return fallback;

    const routeWithoutQuery = route.split("?")[0].split("#")[0];
    // Never restore force-password-change as a return target after login.
    if (routeWithoutQuery.startsWith(FORCE_PASSWORD_CHANGE_ROUTE)) {
      return fallback;
    }

    if (!routeWithoutQuery.startsWith("/workspace")) {
      return route;
    }

    const routeSegments = routeWithoutQuery.split("/").filter(Boolean);

    // A valid workspace route requires: /workspace/:workspaceName/:childRoute
    if (routeSegments.length < 3) {
      return fallback;
    }

    return route;
  }

  popDialog() {
    if (!this.dialogRef) {
      this.dialogRef = this.dialog.open(AuthDialogComponent, {
        width: `400px`,
        disableClose: true,
        backdropClass: "backdrop-background",
      });
    }
  }

  setReAuthTimeout(timeout: number) {
    if (this.timer) {
      window.clearTimeout(this.timer);
    }
    this.timer = window.setTimeout(() => {
      this.logout();
    }, timeout);
  }
}
